import React, { useState, useEffect } from "react";
import { Post } from "./PostTable";
import { ChevronDownIcon, ChevronRightIcon, XMarkIcon, PlusIcon, ArrowDownTrayIcon, ClipboardDocumentIcon, ArrowTopRightOnSquareIcon, PencilIcon } from "@heroicons/react/24/outline";
import { formatDate } from "../../../utilities/format";
import { useOmniUpdatePostMetaMutation, useOmniDeletePostMutation, useOmniMarkMultiplePostsReadyMutation, useOmniRegisterDistributionMutation, useOmniDistributionsQuery, useOmniUpdateDistributionUrlMutation, useOmniDeletePostPhotoMutation } from "../../../lib/apollo/graphql/omni/omni-generated";
import { useAdminUserContext } from "../../common/providers/AdminUserProvider";
import { usePostPhotoUpload } from "../hooks/usePostPhotoUpload";
import { useSnapshot } from "../hooks/useSnapshot";
import SnapshotHistoryModal from "./SnapshotHistoryModal";

interface PostDetailPageProps {
  post: Post | null;
  onClose: () => void;
  onSave?: (post: Post) => void;
  onDelete?: (postId: string) => void;
  onRefresh?: () => void;
}


const STATUS_LABELS: Record<string, string> = {
  EDITING: "검토중",
  READY: "게시예정",
  SCHEDULED: "게시예정",
  PENDING: "보류",
  PUBLISHED: "게시완료",
  DELETED: "삭제됨",
};

const STATUS_COLORS: Record<string, string> = {
  EDITING: "bg-orange-100 text-orange-800 border border-orange-200",
  READY: "bg-blue-100 text-blue-800 border border-blue-200",
  SCHEDULED: "bg-blue-100 text-blue-800 border border-blue-200",
  PENDING: "bg-gray-100 text-gray-800 border border-gray-200",
  PUBLISHED: "bg-green-100 text-green-800 border border-green-200",
  DELETED: "bg-red-100 text-red-800 border border-red-200",
};

export default function PostDetailPage({
  post,
  onClose,
  onSave,
  onDelete,
  onRefresh,
}: PostDetailPageProps) {
  const [editedPost, setEditedPost] = useState<Post | null>(post);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [updatePostMeta, { loading: updateLoading }] = useOmniUpdatePostMetaMutation();
  const [deletePost, { loading: deleteLoading }] = useOmniDeletePostMutation();
  const [markPostsReady] = useOmniMarkMultiplePostsReadyMutation();
  const [registerDistribution, { loading: registeringDistribution }] = useOmniRegisterDistributionMutation();
  const [updateDistributionUrl, { loading: updatingDistribution }] = useOmniUpdateDistributionUrlMutation();
  const [deletePostPhoto] = useOmniDeletePostPhotoMutation();
  const { user } = useAdminUserContext();

  // distributions 쿼리 - postId로 distribution 조회
  const { data: distributionsData, refetch: refetchDistributions } = useOmniDistributionsQuery({
    variables: {
      filters: {
        postId: post?.postId,
      }
    },
    skip: !post?.postId,
    fetchPolicy: 'network-only', // 항상 서버에서 최신 데이터 가져오기
  });

  const distribution = distributionsData?.distributions?.items?.[0];

  const [expandedSections, setExpandedSections] = useState({
    photos: false,
    link: false,
    performance: false,
  });
  const [editingLink, setEditingLink] = useState(false);
  const [tempLink, setTempLink] = useState("");
  const [originalLink, setOriginalLink] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [isMemoChanged, setIsMemoChanged] = useState(false);

  // Post photo upload hook
  const { photos, uploading, uploadPhoto, refetchPhotos } = usePostPhotoUpload(post?.postId || "");

  // 사진 목록 콘솔 출력
  useEffect(() => {
    console.log('=== 게시글 상세 사진 목록 ===');
    console.log('Post ID:', post?.postId);
    console.log('사진 개수:', photos.length);
    console.log('사진 데이터:', photos.map((photo: any) => ({
      id: photo.id,
      objectName: photo.objectName,
      signedUrl: photo.signedUrl,
      altText: photo.altText,
      deletedAt: photo.deletedAt,
    })));
    console.log('===========================');
  }, [photos, post?.postId]);

  // Snapshot hook for performance metrics
  const { latestSnapshot, isCreating, createSnapshot } = useSnapshot(distribution?.id || null);

  // State for snapshot
  const [isEditingSnapshot, setIsEditingSnapshot] = useState(false);
  const [snapshotValue, setSnapshotValue] = useState("");

  // State for image modal
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [snapshotNote, setSnapshotNote] = useState("");
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // 샘플 태그 데이터
  const tags = ["마케팅", "홍보", "콘텐츠", "소셜미디어"];

  // post prop이 변경되면 editedPost 상태도 업데이트
  useEffect(() => {
    if (post) {
      setEditedPost(post);
      setIsMemoChanged(false); // post가 변경되면 memo 변경 상태 초기화
    }
  }, [post]);

  // distribution이 변경되면 링크 상태 업데이트
  useEffect(() => {
    if (distribution?.url) {
      setOriginalLink(distribution.url);
      setTempLink(distribution.url);
    } else {
      setOriginalLink("");
      setTempLink("");
    }
    setEditingLink(false);
  }, [distribution]);

  if (!post || !editedPost) return null;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleStatusChange = (value: string) => {
    setEditedPost({ ...editedPost, status: value as Post['status'] });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleMemoChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedPost({ ...editedPost, memo: e.target.value });
    setIsMemoChanged(true);
  };


  const handleMemoSave = async () => {
    if (!editedPost || !isMemoChanged) return;

    try {
      await updatePostMeta({
        variables: {
          input: {
            postId: editedPost.postId,
            memo: editedPost.memo || "",
          },
        },
      });

      setIsMemoChanged(false);
      alert('메모가 저장되었습니다.');
    } catch (error) {
      console.error('Failed to update post meta:', error);
      alert('메모 저장에 실패했습니다.');
    }
  };

  const handleCopyContent = () => {
    // HTML 태그 제거하고 텍스트만 복사
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = editedPost.content || '';
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    navigator.clipboard.writeText(textContent).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const handleMarkReady = async () => {
    if (!editedPost || !user?.id) return;

    if (window.confirm("이 게시글을 게시예정으로 처리하시겠습니까?")) {
      try {
        await markPostsReady({
          variables: {
            input: {
              postIds: [editedPost.postId],
            },
          },
        });

        alert("게시예정으로 처리되었습니다.");
        if (onRefresh) {
          await onRefresh();
        }
      } catch (error) {
        console.error('Failed to mark post as ready:', error);
        const errorMessage = error instanceof Error ? error.message : '게시예정 처리에 실패했습니다.';
        alert(`처리 실패: ${errorMessage}`);
      }
    }
  };

  const handleDeletePost = async () => {
    if (!editedPost || !user?.id) return;

    if (window.confirm("정말로 이 게시글을 삭제하시겠습니까?")) {
      try {
        await deletePost({
          variables: {
            input: {
              postId: editedPost.postId,
            },
          },
        });

        // 삭제 성공 시 onDelete 콜백 호출 후 바로 닫기 (목록 새로고침 없이)
        if (onDelete) {
          onDelete(editedPost.postId);
        }
        onClose();
      } catch (error) {
        console.error('Failed to delete post:', error);
        const errorMessage = error instanceof Error ? error.message : '게시글 삭제에 실패했습니다.';
        alert(`삭제 실패: ${errorMessage}`);
      }
    }
  };

  const handleClose = async () => {
    // 닫기 전에 목록 새로고침
    if (onRefresh) {
      await onRefresh();
    }
    onClose();
  };

  const toggleSection = (section: 'photos' | 'link' | 'performance') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleImageDelete = async (photoId: string) => {
    if (!editedPost?.postId) return;

    if (window.confirm("이 사진을 삭제하시겠습니까?")) {
      try {
        await deletePostPhoto({
          variables: {
            photoId,
            postId: editedPost.postId,
          },
        });

        alert("사진이 삭제되었습니다.");

        // 사진 목록 새로고침
        await refetchPhotos();
      } catch (error) {
        console.error('Failed to delete photo:', error);

        // GraphQL 에러 메시지 추출
        let errorMessage = '사진 삭제에 실패했습니다.';
        if (error && typeof error === 'object' && 'message' in error) {
          errorMessage = (error as Error).message;

          // 외래키 제약 조건 에러인 경우
          if (errorMessage.includes('Foreign key constraint') || errorMessage.includes('storageFileId')) {
            errorMessage = '사진 삭제에 실패했습니다. 참조 중인 데이터가 있습니다.\n백엔드 팀에 문의해주세요.';
          }
        }

        alert(`삭제 실패: ${errorMessage}`);
      }
    }
  };

  const handleImageUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;

    input.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files || files.length === 0) return;

      try {
        for (const file of Array.from(files)) {
          await uploadPhoto(file);
        }
        alert(`${files.length}개의 이미지가 업로드되었습니다.`);
      } catch (error) {
        console.error('Failed to upload images:', error);
        alert('이미지 업로드에 실패했습니다.');
      }
    };

    input.click();
  };

  const handleDownloadAll = async () => {
    if (photos.length === 0) {
      alert("다운로드할 이미지가 없습니다.");
      return;
    }

    for (const photo of photos) {
      try {
        if (!photo.signedUrl) continue;

        const response = await fetch(photo.signedUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = photo.objectName || `photo-${photo.id}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);

        // 브라우저가 여러 다운로드를 처리할 수 있도록 잠시 대기
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`Failed to download photo ${photo.id}:`, error);
      }
    }
  };

  // 링크 등록 (distribution이 없을 때)
  const handleLinkRegister = async () => {
    if (!editedPost || !tempLink.trim()) {
      alert("링크를 입력해주세요.");
      return;
    }

    // EDITING 상태 체크
    if (editedPost.status === 'EDITING') {
      alert("게시예정 처리를 먼저 해주세요.");
      return;
    }

    // URL 형식 검증
    if (!tempLink.startsWith('http://') && !tempLink.startsWith('https://')) {
      alert("http:// 또는 https://로 시작하는 정확한 url을 입력하세요.");
      return;
    }

    try {
      const adminEmail = 'carelogue@gmail.com';
      await registerDistribution({
        variables: {
          input: {
            channelId: editedPost.channelId,
            postId: editedPost.postId,
            url: tempLink,
            createdBy: adminEmail,
          },
        },
      });

      // distributions 데이터 다시 조회
      await refetchDistributions();

      if (onRefresh) {
        await onRefresh();
      }

      alert("링크가 등록되었고 게시완료 상태로 변경되었습니다.");
    } catch (error) {
      console.error('Failed to register distribution:', error);
      const errorMessage = error instanceof Error ? error.message : '링크 등록에 실패했습니다.';
      alert(`등록 실패: ${errorMessage}`);
    }
  };

  // 링크 수정 (distribution이 있을 때)
  const handleLinkUpdate = async () => {
    if (!distribution || !tempLink.trim()) {
      alert("링크를 입력해주세요.");
      return;
    }

    // 수정사항이 없으면 비활성 상태로 복귀
    if (tempLink === originalLink) {
      setEditingLink(false);
      return;
    }

    // URL 형식 검증
    if (!tempLink.startsWith('http://') && !tempLink.startsWith('https://')) {
      alert("http:// 또는 https://로 시작하는 정확한 url을 입력하세요.");
      return;
    }

    // 수정사항이 있으면 확인
    if (!window.confirm("수정사항을 저장하시겠습니까?")) {
      return;
    }

    try {
      await updateDistributionUrl({
        variables: {
          distributionId: distribution.id,
          input: {
            url: tempLink,
          },
        },
      });

      // distributions 데이터 다시 조회
      await refetchDistributions();

      if (onRefresh) {
        await onRefresh();
      }

      setEditingLink(false);
      alert("링크가 수정되었습니다.");
    } catch (error) {
      console.error('Failed to update distribution:', error);
      const errorMessage = error instanceof Error ? error.message : '링크 수정에 실패했습니다.';
      alert(`수정 실패: ${errorMessage}`);
    }
  };

  // 초기화
  const handleLinkReset = () => {
    setTempLink(originalLink);
  };

  // Snapshot handlers
  const handleAddSnapshot = () => {
    setIsEditingSnapshot(true);
    setSnapshotValue("");
    setSnapshotNote("");
  };

  const handleCancelSnapshot = () => {
    setIsEditingSnapshot(false);
    setSnapshotValue("");
    setSnapshotNote("");
  };

  const handleSaveSnapshot = async () => {
    if (!snapshotValue || !user?.id) {
      alert("조회수를 입력해주세요.");
      return;
    }

    const value = parseInt(snapshotValue);
    if (isNaN(value) || value < 0) {
      alert("올바른 숫자를 입력해주세요.");
      return;
    }

    try {
      await createSnapshot(value, snapshotNote, user.id);
      setIsEditingSnapshot(false);
      setSnapshotValue("");
      setSnapshotNote("");
      alert("조회수가 등록되었습니다.");
    } catch (error) {
      console.error("Failed to create snapshot:", error);
      alert("조회수 등록에 실패했습니다.");
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* 메인 컨텐츠 영역 */}
      <div className="flex">
        {/* 좌측 영역 (5/8) */}
        <div className="flex-[5] bg-white border-r border-gray-200">
          <div className="p-8">
              {/* 기본정보 섹션 - 타이틀과 회색 배경 제거 */}
              <div className="mb-8">
                {/* 채널 */}
                <div className="mb-4 flex items-center">
                  <label className="text-sm font-medium text-gray-700" style={{ width: '100px' }}>채널</label>
                  <div className="inline-flex items-center px-4 py-1 bg-black text-white rounded-full text-sm font-medium">
                    {editedPost.channel || '-'}
                  </div>
                </div>

                {/* 상태 */}
                <div className="mb-4 flex items-center">
                  <label className="text-sm font-medium text-gray-700" style={{ width: '100px' }}>상태</label>
                  <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold border ${STATUS_COLORS[editedPost.status]} text-center`}>
                    {STATUS_LABELS[editedPost.status]}
                  </span>
                </div>

                {/* 생성일시 */}
                <div className="mb-4 flex items-center">
                  <label className="text-sm font-medium text-gray-700" style={{ width: '100px' }}>생성일시</label>
                  <div className="text-sm text-gray-600">
                    {formatDate(editedPost.createdAt, "YYYY.MM.DD HH:mm")}
                  </div>
                </div>

                {/* 수정일시 */}
                <div className="mb-4 flex items-center">
                  <label className="text-sm font-medium text-gray-700" style={{ width: '100px' }}>수정일시</label>
                  <div className="text-sm text-gray-600">
                    {formatDate(editedPost.updatedAt, "YYYY.MM.DD HH:mm")}
                  </div>
                </div>

                {/* 최종 수정자 */}
                <div className="mb-4 flex items-center">
                  <label className="text-sm font-medium text-gray-700" style={{ width: '100px' }}>최종 수정자</label>
                  <div className="text-sm text-gray-600">{editedPost.updatedBy || "-"}</div>
                </div>

                {/* 태그 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">태그</label>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag, index) => (
                      <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* 메모 */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">메모</label>
                    <button
                      onClick={handleMemoSave}
                      disabled={!isMemoChanged}
                      className={`px-3 py-1 text-sm rounded ${
                        isMemoChanged
                          ? 'bg-blue-500 text-white hover:bg-blue-600'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      저장
                    </button>
                  </div>
                  <input
                    type="text"
                    value={editedPost.memo || ""}
                    onChange={(e) => {
                      setEditedPost({ ...editedPost, memo: e.target.value });
                      setIsMemoChanged(true);
                    }}
                    className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-300"
                    placeholder="메모를 입력하세요..."
                  />
                </div>
              </div>

              {/* 제목 섹션 */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">제목</h2>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(editedPost.title);
                      alert('제목이 복사되었습니다.');
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    title="제목 복사"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4" />
                    복사
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="text-sm text-gray-900">
                    {editedPost.title}
                  </div>
                </div>
              </div>

              {/* 내용 섹션 */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">내용</h2>
                  <button
                    onClick={handleCopyContent}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    title="내용 복사"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4" />
                    {copySuccess ? "복사됨!" : "복사"}
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg p-4 bg-white h-[300px] overflow-y-auto">
                  <div
                    className="prose prose-sm max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{ __html: editedPost.content || '<p class="text-gray-400">내용이 없습니다.</p>' }}
                  />
                </div>
              </div>

            {/* 버튼 영역 */}
            <div className="mt-8 pb-16">
              <div className="flex justify-between items-center">
                <div className="flex gap-3">
                  <button
                    onClick={handleMarkReady}
                    className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    게시예정 처리
                  </button>
                  <button
                    onClick={handleDeletePost}
                    disabled={deleteLoading}
                    className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleteLoading ? "삭제 중..." : "삭제"}
                  </button>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleClose}
                    className="px-6 py-2 border border-black text-black rounded-lg hover:bg-gray-100"
                  >
                    목록으로
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 우측 영역 (3/8) */}
        <div className="flex-[3] bg-white">
          <div className="p-8" style={{ paddingBottom: '300px' }}>
          {/* 사진 섹션 */}
          <div className="mb-6 border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleSection('photos')}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <span className="font-medium">사진 ({photos.length})</span>
              {expandedSections.photos ? (
                <ChevronDownIcon className="w-5 h-5" />
              ) : (
                <ChevronRightIcon className="w-5 h-5" />
              )}
            </button>

            {expandedSections.photos && (
              <div className="p-4 border-t border-gray-200">
                {/* 이미지 썸네일 그리드 (3열) */}
                <div className="mb-4">
                  <div className="grid grid-cols-3 gap-3">
                    {photos.map((photo: any) => (
                      <div key={photo.id} className="relative group">
                        <img
                          src={photo.signedUrl || ''}
                          alt={photo.altText || photo.objectName || ''}
                          className="w-full h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => setSelectedImage(photo.signedUrl || '')}
                        />
                        <button
                          onClick={() => handleImageDelete(photo.id)}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                        <div className="mt-1 text-xs text-gray-500 truncate">
                          {photo.objectName || photo.id}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 버튼들 */}
                <div className="flex gap-2">
                  <button
                    onClick={handleDownloadAll}
                    disabled={photos.length === 0}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                    전체 다운로드
                  </button>
                  <button
                    onClick={handleImageUpload}
                    disabled={uploading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <PlusIcon className="w-4 h-4" />
                    {uploading ? '업로드 중...' : '추가 업로드'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 링크 섹션 */}
          <div className="mb-6 border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleSection('link')}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <span className="font-medium">링크</span>
              {expandedSections.link ? (
                <ChevronDownIcon className="w-5 h-5" />
              ) : (
                <ChevronRightIcon className="w-5 h-5" />
              )}
            </button>
            
            {expandedSections.link && (
              <div className="p-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    게시된 채널 링크
                  </label>
                  {distribution && !editingLink && (
                    <button
                      onClick={() => window.open(distribution.url, '_blank')}
                      className="p-1 hover:bg-gray-100 rounded"
                      title="새 창에서 열기"
                    >
                      <ArrowTopRightOnSquareIcon className="w-5 h-5 text-gray-600" />
                    </button>
                  )}
                </div>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={tempLink}
                    onChange={(e) => setTempLink(e.target.value)}
                    disabled={distribution && !editingLink}
                    className={`flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      distribution && !editingLink ? 'bg-gray-50 cursor-not-allowed' : ''
                    }`}
                    placeholder="https://example.com/post/..."
                  />
                  {distribution && !editingLink && (
                    <button
                      onClick={() => setEditingLink(true)}
                      className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center justify-center"
                      title="수정"
                    >
                      <PencilIcon className="w-5 h-5 text-gray-600" />
                    </button>
                  )}
                  {!distribution && !editingLink && (
                    <button
                      onClick={handleLinkRegister}
                      disabled={registeringDistribution || !tempLink.trim()}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {registeringDistribution ? '저장 중...' : '저장'}
                    </button>
                  )}
                </div>
                {editingLink && (
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={handleLinkReset}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      초기화
                    </button>
                    <button
                      onClick={handleLinkUpdate}
                      disabled={updatingDistribution}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {updatingDistribution ? '저장 중...' : '저장'}
                    </button>
                  </div>
                )}

                {distribution && (
                  <div className="mt-4 pt-3 border-t border-gray-200 text-xs text-gray-500 space-y-2">
                    <div>
                      <div className="font-medium mb-1">최초 등록자</div>
                      <div>{distribution.createdBy} · {formatDate(distribution.createdAt, "YYYY.MM.DD HH:mm")}</div>
                    </div>
                    <div>
                      <div className="font-medium mb-1">최종 수정자</div>
                      <div>{(distribution as any).updatedBy || '(정보없음)'} · {formatDate(distribution.updatedAt, "YYYY.MM.DD HH:mm")}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 성과 섹션 */}
          <div className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleSection('performance')}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <span className="font-medium">성과</span>
              {expandedSections.performance ? (
                <ChevronDownIcon className="w-5 h-5" />
              ) : (
                <ChevronRightIcon className="w-5 h-5" />
              )}
            </button>

            {expandedSections.performance && (
              <div className="p-4 border-t border-gray-200">
                {!distribution ? (
                  <div className="text-sm text-gray-500 text-center py-4">
                    링크가 등록되면 조회수를 입력할 수 있습니다.
                  </div>
                ) : isEditingSnapshot || !latestSnapshot ? (
                  /* 등록 모드 */
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        조회수 *
                      </label>
                      <input
                        type="number"
                        value={snapshotValue}
                        onChange={(e) => setSnapshotValue(e.target.value)}
                        placeholder="숫자를 입력하세요"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        메모
                      </label>
                      <input
                        type="text"
                        value={snapshotNote}
                        onChange={(e) => setSnapshotNote(e.target.value)}
                        placeholder="메모를 입력하세요"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      {isEditingSnapshot && (
                        <button
                          onClick={handleCancelSnapshot}
                          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                        >
                          취소
                        </button>
                      )}
                      <button
                        onClick={handleSaveSnapshot}
                        disabled={isCreating || !snapshotValue}
                        className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        {isCreating ? "저장 중..." : "저장"}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* 조회 모드 */
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700">조회</label>
                      <div className="text-lg font-semibold text-gray-900">
                        {latestSnapshot.value.toLocaleString()}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 space-y-1">
                      {latestSnapshot.createdBy && <div>등록자: {latestSnapshot.createdBy}</div>}
                      <div>등록일시: {formatDate(latestSnapshot.capturedAt, "YYYY.MM.DD HH:mm")}</div>
                      {latestSnapshot.note && <div>메모: {latestSnapshot.note}</div>}
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={handleAddSnapshot}
                        className="flex-1 px-4 py-2 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-50"
                      >
                        등록하기
                      </button>
                      <button
                        onClick={() => setShowHistoryModal(true)}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      >
                        내역보기
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      </div>

      {/* Snapshot History Modal */}
      {showHistoryModal && distribution && (
        <SnapshotHistoryModal
          distributionId={distribution.id}
          onClose={() => setShowHistoryModal(false)}
        />
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-7xl max-h-screen p-4">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-6 right-6 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="w-6 h-6 text-gray-700" />
            </button>
            <img
              src={selectedImage}
              alt="확대 이미지"
              className="max-w-full max-h-screen object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}